import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import { Box, Typography, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { uploadCSV } from "../../../assets";

import * as Yup from "yup";
import { Form, Formik } from "formik";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import Button from "../../../component-lib/Molecules/button/Button";

interface ImportInventoryProps {
  handleOnSubmittedData?: (formData: { file: File | null }) => void;
  handleClose: () => void;
  isImporting?: boolean;
  importMessage?: string | null;
}

const ImportInventory = ({
  handleOnSubmittedData,
  handleClose,
  isImporting = false,
  importMessage = null,
}: ImportInventoryProps) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const validationSchema = Yup.object().shape({
    // Remove file validation since we're using selectedFile state
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      setError("No file selected.");
      return;
    }

    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const allowedExtensions = [".csv", ".xls", ".xlsx"];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."));

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension)
    ) {
      setError("Only CSV, XLS, and XLSX files are allowed.");
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      const allowedTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      const allowedExtensions = [".csv", ".xls", ".xlsx"];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        setError("Only CSV, XLS, and XLSX files are allowed.");
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const downloadSampleExcel = () => {
    // Create sample data based on the provided inventory data
    const sampleData = [
      [
        "itemName",
        "itemCode",
        "type",
        "unitOfMeasure",
        "quantity",
        "skuCode",
        "threshold",
        "supplierName",
        "supplierUnit",
        "batchNumber",
        "expirationDate",
      ],
      [
        "Aspirin Tablets",
        "ASP001",
        "Medicine",
        "Tablets",
        "100",
        "SKU-ASP-001",
        "20",
        "MedSupply Inc",
        "Box of 100",
        "BATCH-2024-001",
        "2025-12-31",
      ],
      [
        "Bandages (Medium)",
        "BAND002",
        "First Aid",
        "Pieces",
        "50",
        "SKU-BAND-002",
        "10",
        "FirstAid Co",
        "Box of 50",
        "BATCH-2024-002",
        "2026-06-15",
      ],
      [
        "Antiseptic Solution",
        "ANT003",
        "First Aid",
        "Bottles",
        "25",
        "SKU-ANT-003",
        "5",
        "HealthSupply Ltd",
        "Box of 25",
        "BATCH-2024-003",
        "2025-08-20",
      ],
      [
        "Pain Relief Gel",
        "GEL004",
        "Medicine",
        "Tubes",
        "30",
        "SKU-GEL-004",
        "8",
        "PharmaCorp",
        "Box of 30",
        "BATCH-2024-004",
        "2026-03-10",
      ],
      [
        "Medical Gloves (Box)",
        "GLOV005",
        "First Aid",
        "Pairs",
        "200",
        "SKU-GLOV-005",
        "50",
        "SafetyFirst Inc",
        "Box of 200",
        "BATCH-2024-005",
        "2025-11-30",
      ],
      [
        "Thermometer Digital",
        "TEMP006",
        "First Aid",
        "Pieces",
        "5",
        "SKU-TEMP-006",
        "2",
        "MedTech Solutions",
        "Box of 5",
        "BATCH-2024-006",
        "2027-01-15",
      ],
      [
        "First Aid Kit",
        "KIT007",
        "First Aid",
        "Kits",
        "10",
        "SKU-KIT-007",
        "3",
        "Emergency Supplies",
        "Box of 10",
        "BATCH-2024-007",
        "2026-09-30",
      ],
      [
        "Disinfectant Wipes",
        "WIPE008",
        "First Aid",
        "Packs",
        "75",
        "SKU-WIPE-008",
        "15",
        "CleanCare Ltd",
        "Box of 75",
        "BATCH-2024-008",
        "2025-10-25",
      ],
      [
        "Surgical Masks",
        "MASK009",
        "First Aid",
        "Pieces",
        "150",
        "SKU-MASK-009",
        "30",
        "Protective Gear Co",
        "Box of 150",
        "BATCH-2024-009",
        "2025-07-18",
      ],
      [
        "Blood Pressure Cuff",
        "BP010",
        "First Aid",
        "Pieces",
        "3",
        "SKU-BP-010",
        "1",
        "Medical Devices Inc",
        "Box of 3",
        "BATCH-2024-010",
        "2026-12-31",
      ],
    ];

    // Convert to CSV format for Excel compatibility
    const csvContent = sampleData.map((row) => row.join(",")).join("\n");

    // Create Excel-compatible blob
    const blob = new Blob([csvContent], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "inventory_sample.xlsx");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Formik
      initialValues={{
        file: null,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        try {
          const formData = { ...values, file: selectedFile };
          handleOnSubmittedData?.(formData);
        } catch (error) {
          console.log("Error in onSubmit:", error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <Form
            onSubmit={handleSubmit}
            className="h-full flex flex-col justify-between rounded-lg"
          >
            <div className="w-full flex flex-col gap-6 rounded-lg">
              {/* FILE UPLOAD SECTION */}
              <div>
                {/* <Heading title="Import Inventory" customStyle={headerStyle} /> */}
                <div className="flex flex-col gap-4">
                  {/* CSV Upload Area */}
                  <Box textAlign="start">
                    <button
                      type="button"
                      className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors cursor-pointer w-full bg-transparent ${
                        isDragOver
                          ? "border-primary-main bg-primary-light"
                          : "border-neutral-30 hover:border-primary-main"
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() =>
                        document.getElementById("csv-upload")?.click()
                      }
                    >
                      <img
                        src={uploadCSV}
                        alt="Upload CSV"
                        className="mb-4 w-24 h-24"
                      />
                      <Typography
                        variant="h6"
                        className="text-primary-main underline cursor-pointer"
                      >
                        Upload File
                      </Typography>
                      <Typography
                        variant="body2"
                        className="text-neutral-60 mt-2"
                      >
                        Click to select or drag and drop your file here
                      </Typography>
                      <Typography
                        variant="caption"
                        className="text-neutral-50 mt-1"
                      >
                        Supported formats: CSV, XLS, XLSX
                      </Typography>
                    </button>
                    <input
                      type="file"
                      accept=".csv,.xls,.xlsx"
                      id="csv-upload"
                      hidden
                      onChange={handleFileChange}
                    />

                    {error && (
                      <Typography color="error" className="mt-2">
                        {error}
                      </Typography>
                    )}

                    {importMessage && (
                      <Typography
                        color={
                          importMessage.includes("error") ||
                          importMessage.includes("Failed")
                            ? "error"
                            : "success"
                        }
                        className="mt-2"
                      >
                        {importMessage}
                      </Typography>
                    )}

                    {selectedFile && (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        className="mt-4 p-3 bg-neutral-10 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Typography variant="body2" className="font-medium">
                            {selectedFile.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            className="text-neutral-60"
                          >
                            ({(selectedFile.size / 1024).toFixed(1)} KB)
                          </Typography>
                        </div>
                        <IconButton
                          onClick={handleRemoveFile}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {/* Instructions Section */}
                  <div className="bg-primary-light p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <Typography
                        variant="body2"
                        className=" text-neutral-63 mb-2"
                        style={{ fontSize: "14px" }}
                      >
                        File Format Requirements:
                      </Typography>
                      <Button
                        label="Download Sample File"
                        variant="outlined"
                        onClickCb={downloadSampleExcel}
                        style={{
                          minWidth: "140px",
                          height: "32px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          backgroundColor: "white",
                          border: "1px solid #005596",
                          color: "#005596",
                          fontFamily: "Inter",
                        }}
                      />
                    </div>
                    <ul className="text-sm text-neutral-60 space-y-1">
                      <li>
                        • File must be in CSV (.csv), XLS (.xls), or XLSX
                        (.xlsx) format
                      </li>
                      <li>
                        • Include headers: itemName, itemCode, type,
                        unitOfMeasure, quantity, skuCode, threshold,
                        supplierName, supplierUnit, batchNumber, expirationDate
                      </li>
                      <li>• Maximum file size: 10MB</li>
                      <li>• Ensure all required fields are filled</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-10 gap-4">
              <Button
                label={TEXT_CONSTANTS?.CANCEL}
                variant="outlined"
                onClickCb={() => {
                  handleClose();
                }}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: "white",
                  border: "1px solid #005596",
                  color: "#005596",
                  fontFamily: "Inter",
                }}
              />
              <Button
                disabled={!selectedFile || isSubmitting || isImporting}
                type="button"
                label={isImporting ? "Importing..." : "Import Inventory"}
                variant="contained"
                onClickCb={() => {
                  if (!selectedFile) {
                    setError("Please select a file to import");
                    return;
                  }
                  handleSubmit();
                }}
                style={{
                  width: "200px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: isImporting ? "#ccc" : "#005596",
                  color: "#FFFFFF",
                  border: "1px solid #005596",
                  fontFamily: "Inter",
                }}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

// IMPORT INVENTORY DIALOG

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "auto",
    width: "708px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface ImportInventoryDialogProps {
  isOpen: boolean;
  dialogHeading: string;
  handleClose: () => void;
  handleImportInventoryData: (data: { file: File | null }) => void;
  isImporting?: boolean;
  importMessage?: string | null;
}

export default function ImportInventoryDialog({
  isOpen = false,
  handleClose,
  handleImportInventoryData,
  dialogHeading = "Import Inventory",
  isImporting = false,
  importMessage = null,
}: Readonly<ImportInventoryDialogProps>) {
  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <div
        className="flex justify-between items-center rounded-lg"
        style={{ margin: "14px 24px" }}
      >
        <Heading title={dialogHeading} />
        <button
          onClick={handleClose}
          className="cursor-pointer border-none bg-transparent p-0"
          type="button"
          aria-label="Close dialog"
        >
          <CloseIcon />
        </button>
      </div>

      <DialogContent
        dividers
        style={{ padding: "16px 24px 24px 24px", borderRadius: "8px" }}
      >
        <ImportInventory
          handleClose={handleClose}
          handleOnSubmittedData={(formData) => {
            handleImportInventoryData(formData);
          }}
          isImporting={isImporting}
          importMessage={importMessage}
        />
      </DialogContent>
    </BootstrapDialog>
  );
}
